import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { resolveMediaUrl } from '@/lib/api';
import { useLanguage } from '@/context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BrochureButton = ({ propertyId, propertyTitle, brochureFile, className }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [granted, setGranted] = useState(null); // brochure_url after submission
  const [form, setForm] = useState({ name: '', mobile: '', email: '', message: '' });
  const { t } = useLanguage();

  if (!brochureFile) return null;

  const trigger = () => {
    if (granted) {
      window.open(resolveMediaUrl(granted), '_blank', 'noopener');
      return;
    }
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) {
      toast.error('Name and mobile are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { property_id: propertyId, ...form };
      // Omit empty optional fields — backend EmailStr rejects ''
      if (!payload.email) delete payload.email;
      if (!payload.message) delete payload.message;
      const { data } = await axios.post(`${API}/brochure/inquiry`, payload);
      toast.success(t('form.submitted', 'Thank you! Brochure unlocked.'));
      setGranted(data.brochure_url);
      setOpen(false);
      // Auto-trigger download
      window.open(resolveMediaUrl(data.brochure_url), '_blank', 'noopener');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        onClick={trigger}
        className={className || 'bg-gradient-to-r from-brand-orange to-brand-rust text-white rounded-full'}
        data-testid="download-brochure-btn"
      >
        <FileText className="h-4 w-4 mr-2" />
        {t('cta.download_brochure', 'Download Brochure')}
        <Download className="h-4 w-4 ml-2" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('form.brochure_inquiry_title', 'Get Free Brochure')}</DialogTitle>
            <DialogDescription>
              {t('form.brochure_inquiry_subtitle', 'Fill the form to instantly download the brochure')}
              {propertyTitle ? ` — ${propertyTitle}` : ''}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1">
              <Label>{t('form.name', 'Name')} *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="brochure-name" />
            </div>
            <div className="space-y-1">
              <Label>{t('form.mobile', 'Mobile')} *</Label>
              <Input type="tel" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} required data-testid="brochure-mobile" />
            </div>
            <div className="space-y-1">
              <Label>{t('form.email', 'Email')}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="brochure-email" />
            </div>
            <div className="space-y-1">
              <Label>{t('form.message', 'Message')}</Label>
              <Textarea rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-brand-orange to-brand-rust text-white" data-testid="brochure-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
              {t('cta.submit_enquiry', 'Submit & Download')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
